import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { WEEKDAY_SHORT, parseISODate, today } from '../lib/dates';
import { MEAL_TYPES, kcal } from '../lib/format';
import { isPlannedMealLogged, planDates, planRange } from '../lib/plans';
import { useApp } from '../lib/AppContext';

// Sắp xếp tuần: the week as a board, 7 days × 4 meals. Tap a dish to lift it, tap another slot to put it
// down: onto a dish swaps the two, onto an empty slot moves it there. A long press drags it instead.
// Each move is saved at once (no AI, no credits); Hoàn tác steps back through this session's moves.
// Past days and dishes already logged today are locked. Day totals update live, with the change for a moment.

const SLOT = { breakfast: 'Sáng', lunch: 'Trưa', dinner: 'Tối', snack: 'Vặt' };
const HOLD_MS = 350;
const DELTA_MS = 3000;
let uidCounter = 0;
const withIds = (plan) => (plan.plan.days || []).map((day) => day.meals.map((meal) => ({ ...meal, uid: `m${uidCounter += 1}` })));
const total = (meals) => meals.reduce((sum, m) => sum + (Number(m.calories) || 0), 0);
const signed = (n) => (n > 0 ? `+${kcal(n)}` : `−${kcal(-n)}`);
// Keep a day's dishes in slot order (Sáng, Trưa, Tối, Vặt), as the day view lists them.
const insertSorted = (meals, meal) => {
  const at = meals.findIndex((m) => MEAL_TYPES.indexOf(m.meal_type) > MEAL_TYPES.indexOf(meal.meal_type));
  return at < 0 ? [...meals, meal] : [...meals.slice(0, at), meal, ...meals.slice(at)];
};

export default function ArrangeWeek({ plan, todayLog, goal, initialPick, onSaved, onClose }) {
  const { api } = useApp();
  const dates = planDates(plan);
  const todayIso = today();
  const [days, setDays] = useState(() => withIds(plan));
  const [picked, setPicked] = useState(() => (initialPick ? days[initialPick.day]?.[initialPick.meal]?.uid ?? null : null));
  const [history, setHistory] = useState([]);
  const [notice, setNotice] = useState(null); // { title, detail } after a move or undo
  const [deltas, setDeltas] = useState({}); // day index -> kcal change, shown for a moment
  const [error, setError] = useState(null);
  const [closing, setClosing] = useState(false);
  const gridRef = useRef(null);
  const saving = useRef(Promise.resolve());

  const dayLabel = (i) => `${WEEKDAY_SHORT[(parseISODate(dates[i]).getDay() + 6) % 7]} · ${parseISODate(dates[i]).getDate()}`;
  const dayShort = (i) => WEEKDAY_SHORT[(parseISODate(dates[i]).getDay() + 6) % 7];
  const dayLocked = (i) => dates[i] < todayIso;
  const isLogged = (i, meal) => dates[i] === todayIso && isPlannedMealLogged(todayLog, meal);
  const locked = (i, meal) => dayLocked(i) || isLogged(i, meal);
  const find = (uid, board = days) => {
    for (let d = 0; d < board.length; d += 1) {
      const m = board[d].findIndex((meal) => meal.uid === uid);
      if (m >= 0) return { d, m, meal: board[d][m] };
    }
    return null;
  };

  // Saves run one after another, each with the whole week, so the last one always wins.
  const save = (board) => {
    const body = { days: board.map((meals) => meals.map(({ uid, ...meal }) => meal)) };
    saving.current = saving.current.then(() => api.patch(`/meal_plans/${plan.id}/arrange`, body))
      .then((updated) => { if (updated) onSaved(updated); setError(null); })
      .catch((e) => setError(e.message));
  };

  // The two dishes slide to their new places (FLIP): remember where every dish was, animate from there.
  const before = useRef(null);
  const snapshot = () => {
    before.current = new Map([...gridRef.current.querySelectorAll('[data-uid]')].map((el) => [el.dataset.uid, el.getBoundingClientRect()]));
  };
  useLayoutEffect(() => {
    const rects = before.current;
    before.current = null;
    if (!rects || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    gridRef.current.querySelectorAll('[data-uid]').forEach((el) => {
      const from = rects.get(el.dataset.uid);
      if (!from) return;
      const to = el.getBoundingClientRect();
      const dx = from.left - to.left;
      const dy = from.top - to.top;
      if (dx || dy) el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 250, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' });
    });
  }, [days]);

  useEffect(() => {
    if (!Object.keys(deltas).length) return undefined;
    const timer = setTimeout(() => setDeltas({}), DELTA_MS);
    return () => clearTimeout(timer);
  }, [deltas]);

  const commit = (next, { title, detail, changedDays }) => {
    snapshot();
    setHistory((h) => [...h, days]);
    setDays(next);
    setPicked(null);
    setNotice({ title, detail });
    // Within one day the total doesn't change, so there's nothing to show.
    setDeltas(changedDays.length > 1 ? Object.fromEntries(changedDays.map((d) => [d, total(next[d]) - total(days[d])])) : {});
    save(next);
  };

  // Put the lifted dish onto a dish (swap) or into an empty slot (move).
  const place = (target) => {
    const from = find(picked);
    if (!from) return;
    const slot = (d, type) => `${dayShort(d)} ${SLOT[type]}`;
    const next = days.map((meals) => [...meals]);
    if (target.uid) {
      const to = find(target.uid);
      if (!to || to.meal.uid === from.meal.uid) return;
      next[from.d][from.m] = { ...to.meal, meal_type: from.meal.meal_type };
      next[to.d][to.m] = { ...from.meal, meal_type: to.meal.meal_type };
      commit(next, {
        title: `Đã đổi ${slot(from.d, from.meal.meal_type)} ↔ ${slot(to.d, to.meal.meal_type)}`,
        detail: `${from.meal.name} ↔ ${to.meal.name}`,
        changedDays: [...new Set([from.d, to.d])],
      });
    } else {
      next[from.d].splice(from.m, 1);
      next[target.d] = insertSorted(next[target.d], { ...from.meal, meal_type: target.type });
      commit(next, {
        title: `Đã chuyển ${slot(from.d, from.meal.meal_type)} → ${slot(target.d, target.type)}`,
        detail: from.meal.name,
        changedDays: [...new Set([from.d, target.d])],
      });
    }
  };

  const undo = () => {
    const previous = history[history.length - 1];
    if (!previous) return;
    snapshot();
    setHistory((h) => h.slice(0, -1));
    setDays(previous);
    setPicked(null);
    setDeltas({});
    setNotice({ title: 'Đã hoàn tác', detail: history.length > 1 ? `Còn ${history.length - 1} bước có thể hoàn tác` : 'Đã về lúc mở Sắp xếp' });
    save(previous);
  };

  const tapDish = (d, meal) => {
    if (locked(d, meal)) return;
    if (!picked) {
      setPicked(meal.uid);
      setNotice(null);
    } else if (picked === meal.uid) {
      setPicked(null);
    } else {
      place({ uid: meal.uid });
    }
  };

  // ---------- Long press to drag (tap–tap stays the main way) ----------
  const [ghost, setGhost] = useState(null); // { meal, x, y } while dragging
  const [hover, setHover] = useState(null); // the slot under the finger
  const drag = useRef(null);
  const skipClick = useRef(false);
  const latest = useRef({});
  latest.current = { place, picked };
  const targetAt = (x, y) => {
    const el = document.elementFromPoint(x, y)?.closest('[data-target]');
    if (!el) return null;
    const [kind, a, b] = el.dataset.target.split(':');
    return kind === 'dish' ? { key: el.dataset.target, uid: a } : { key: el.dataset.target, d: Number(a), type: b };
  };
  const startPress = (e, d, meal) => {
    if (locked(d, meal) || e.button > 0) return;
    const { clientX: x, clientY: y } = e;
    drag.current = {
      x, y, active: false,
      timer: setTimeout(() => {
        drag.current.active = true;
        setPicked(meal.uid);
        setNotice(null);
        setGhost({ meal, x: drag.current.x, y: drag.current.y });
        navigator.vibrate?.(10);
      }, HOLD_MS),
    };
  };
  useEffect(() => {
    const end = () => {
      if (drag.current) clearTimeout(drag.current.timer);
      drag.current = null;
      setGhost(null);
      setHover(null);
    };
    const move = (e) => {
      const state = drag.current;
      if (!state) return;
      if (!state.active) {
        if (Math.hypot(e.clientX - state.x, e.clientY - state.y) > 10) end();
        return;
      }
      state.x = e.clientX;
      state.y = e.clientY;
      setGhost((g) => g && { ...g, x: e.clientX, y: e.clientY });
      setHover(targetAt(e.clientX, e.clientY)?.key ?? null);
    };
    const up = (e) => {
      const state = drag.current;
      if (state?.active) {
        skipClick.current = true;
        const target = targetAt(e.clientX, e.clientY);
        if (target && !(target.uid && target.uid === latest.current.picked)) latest.current.place(target);
      }
      end();
    };
    // Once dragging, the finger moves the dish, not the page.
    const noScroll = (e) => { if (drag.current?.active) e.preventDefault(); };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', end);
    document.addEventListener('touchmove', noScroll, { passive: false });
    return () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', end);
      document.removeEventListener('touchmove', noScroll);
    };
  }, []);
  const click = (fn) => () => {
    if (skipClick.current) {
      skipClick.current = false;
      return;
    }
    fn();
  };

  const finish = useCallback(async () => {
    setClosing(true);
    await saving.current;
    onClose();
  }, [onClose]);

  useEffect(() => {
    const escape = (e) => e.key === 'Escape' && setPicked(null);
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, []);

  const pickedAt = picked ? find(picked) : null;

  return (
    <div className="pt-safe fixed inset-0 z-40 overflow-y-auto overscroll-contain bg-base-200" role="dialog" aria-modal="true" aria-labelledby="arrange-title">
      <div className="mx-auto flex max-w-xl flex-col gap-3 px-4 pb-36 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 id="arrange-title" className="text-[22px] font-extrabold leading-tight">Sắp xếp tuần</h1>
            <p className="tabular text-sm text-muted">{planRange(plan)}{goal ? ` · mục tiêu ≈ ${kcal(goal)} kcal/ngày` : ''}</p>
          </div>
          <button type="button" className="btn btn-primary h-11 shrink-0 rounded-full border-0 px-5 font-bold" disabled={closing} onClick={finish}>
            {closing && <span className="loading loading-spinner loading-sm" />} Xong
          </button>
        </div>

        <p className="flex items-start gap-2 rounded-[16px] bg-base-100 p-3 text-sm text-muted">
          <Icon name="arrange" className="mt-0.5 size-4 shrink-0 text-brand-text" />
          {history.length && Object.keys(deltas).length
            ? 'Tổng kcal của các ngày đã cập nhật. Món đã ăn (xám) không đổi được.'
            : 'Chạm món để nhấc lên, rồi chạm ô khác để đổi chỗ. Ô trống thì món được chuyển sang.'}
        </p>
        {error && <p role="alert" className="rounded-[16px] bg-[#fdebe4] p-3 text-sm text-[#8e3414]">Chưa lưu được: {error}</p>}

        <div ref={gridRef} role="grid" aria-label="Thực đơn tuần" className="grid grid-cols-[56px_repeat(4,minmax(0,1fr))] gap-1.5 select-none">
          <span role="presentation" />
          {MEAL_TYPES.map((type) => <span key={type} role="columnheader" className="text-center text-xs font-semibold text-muted">{SLOT[type]}</span>)}
          {days.map((meals, d) => {
            const sum = total(meals);
            const over = goal && sum > goal && !dayLocked(d);
            const isToday = dates[d] === todayIso;
            return (
              <div key={dates[d]} role="row" className="contents">
                <div role="rowheader" className={`flex flex-col justify-center py-1 pl-1.5 ${isToday ? 'rounded-[4px] border-l-[3px] border-primary' : ''} ${dayLocked(d) ? 'opacity-50' : ''}`}>
                  <span className="whitespace-nowrap text-[12px] font-bold leading-tight">{dayLabel(d)}</span>
                  <span className={`tabular text-[11px] ${over ? 'font-semibold text-[#c2410c]' : 'text-muted'}`}>{kcal(sum)}</span>
                  {deltas[d] ? (
                    <span className={`tabular text-[11px] font-bold ${deltas[d] > 0 ? 'text-[#c2410c]' : 'text-brand-text'}`}>{signed(deltas[d])}</span>
                  ) : null}
                </div>
                {MEAL_TYPES.map((type) => {
                  const inSlot = meals.filter((m) => m.meal_type === type);
                  const label = `${dayShort(d)} ${SLOT[type]}`;
                  if (!inSlot.length) {
                    const target = `empty:${d}:${type}`;
                    const canDrop = !!picked && !dayLocked(d);
                    return (
                      <div key={type} role="gridcell" className="min-h-16">
                        <button type="button" data-target={dayLocked(d) ? undefined : target} disabled={!canDrop}
                          aria-label={`${label}: trống${canDrop ? ', đặt vào đây' : ''}`}
                          onClick={click(() => place({ d, type }))}
                          className={`flex size-full min-h-16 flex-col items-center justify-center rounded-[12px] border-[1.5px] border-dashed px-1 text-center text-[11px] leading-tight
                            ${canDrop ? `border-primary font-semibold text-brand-text ${hover === target ? 'bg-[#e3f1e9]' : 'bg-[#e3f1e9]/40'}` : 'border-line text-muted'}`}>
                          {canDrop ? 'Đặt vào đây' : <><Icon name="plus" className="size-3.5" /> Trống</>}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div key={type} role="gridcell" className="flex min-h-16 flex-col gap-1">
                      {inSlot.map((meal) => {
                        const isPicked = picked === meal.uid;
                        const logged = isLogged(d, meal);
                        const off = locked(d, meal);
                        const target = `dish:${meal.uid}`;
                        return (
                          <button key={meal.uid} type="button" data-uid={meal.uid} data-target={off ? undefined : target}
                            aria-pressed={isPicked} aria-disabled={off}
                            aria-label={`${label}: ${meal.name}, ${kcal(meal.calories)} kcal${isPicked ? ', đang nhấc' : ''}${logged ? ', đã ăn, không đổi được' : off ? ', ngày đã qua' : ''}`}
                            onPointerDown={(e) => startPress(e, d, meal)} onClick={click(() => tapDish(d, meal))}
                            onContextMenu={(e) => e.preventDefault()}
                            className={`relative flex min-h-16 flex-1 flex-col justify-between rounded-[12px] border p-1.5 text-left [-webkit-touch-callout:none]
                              ${off ? 'border-transparent bg-base-300/60 text-muted' : 'bg-base-100'}
                              ${isPicked ? 'z-10 -translate-y-0.5 border-2 border-primary shadow-lg' : ''}
                              ${!isPicked && !off ? (hover === target ? 'border-primary bg-[#e3f1e9]' : 'border-line') : ''}`}>
                            <span className="line-clamp-3 text-[11.5px] font-semibold leading-tight">{meal.name}</span>
                            <span className="tabular text-[10.5px] text-muted">{logged ? 'Đã ăn' : kcal(meal.calories)}</span>
                            {logged && (
                              <span className="absolute -right-1 -top-1 grid size-4 place-items-center rounded-full bg-[#9aa39e] text-white"><Icon name="check" className="size-2.5" /></span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {ghost && (
        <div aria-hidden="true" className="pointer-events-none fixed z-50 w-24 -translate-x-1/2 -translate-y-1/2 rotate-2 rounded-[12px] border-2 border-primary bg-base-100 p-1.5 shadow-xl"
          style={{ left: ghost.x, top: ghost.y }}>
          <span className="line-clamp-2 text-[11.5px] font-semibold leading-tight">{ghost.meal.name}</span>
          <span className="tabular text-[10.5px] text-muted">{kcal(ghost.meal.calories)}</span>
        </div>
      )}

      <div className="pb-safe fixed inset-x-0 bottom-0 z-50 px-4 pb-4">
        <div role="status" aria-live="polite" className="mx-auto max-w-xl">
          {(pickedAt || notice) && (
            <div className="flex items-center gap-3 rounded-[18px] bg-[#17201b] px-4 py-3 text-white shadow-xl">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white/65">
                  {pickedAt ? `Đang nhấc · ${dayShort(pickedAt.d)} ${SLOT[pickedAt.meal.meal_type]}` : notice.title}
                </p>
                <p className="truncate font-bold">
                  {pickedAt ? `${pickedAt.meal.name} · ${kcal(pickedAt.meal.calories)} kcal` : notice.detail}
                </p>
              </div>
              {pickedAt ? (
                <button type="button" className="btn btn-sm h-10 shrink-0 rounded-[12px] border border-white/30 bg-transparent px-4 font-bold text-white" onClick={() => setPicked(null)}>
                  Bỏ chọn
                </button>
              ) : history.length > 0 && (
                <button type="button" className="btn btn-sm h-10 shrink-0 rounded-[12px] border-0 bg-white px-4 font-bold text-[#17201b]" onClick={undo}>
                  Hoàn tác
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
